import { useEffect, useState, useCallback } from 'react';
import { platformAnalytics, platformMdas, type MDA } from '@/services/platform';
import { Button } from '@/components/common/Button';
import styles from './AuditLogsPage.module.css';

interface AuditLog {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  details: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  mda: {
    id: string;
    code: string;
    name: string;
  } | null;
}

interface AuditLogFilters {
  action: string;
  entityType: string;
  mdaId: string;
  search: string;
}

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'UPLOAD', label: 'Upload' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'VIEW', label: 'View' },
  { value: 'APPROVE', label: 'Approve' },
  { value: 'REJECT', label: 'Reject' },
];

const ENTITY_TYPES = [
  { value: '', label: 'All Entities' },
  { value: 'document', label: 'Documents' },
  { value: 'user', label: 'Users' },
  { value: 'mda', label: 'Institutions' },
  { value: 'role', label: 'Roles' },
  { value: 'workflow', label: 'Workflows' },
  { value: 'session', label: 'Sessions' },
];

export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [mdas, setMdas] = useState<MDA[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<AuditLogFilters>({
    action: '',
    entityType: '',
    mdaId: '',
    search: '',
  });
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const perPage = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        page,
        perPage,
      };

      if (filters.action) params.action = filters.action;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.mdaId) params.mdaId = filters.mdaId;

      const response = await platformAnalytics.getAuditLogs(params);

      if (response.success && response.data) {
        setLogs(response.data.data as AuditLog[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    async function fetchMdas() {
      try {
        const response = await platformMdas.list();
        if (response.success && response.data) {
          setMdas(response.data);
        }
      } catch (err) {
        console.error('Failed to load MDAs:', err);
      }
    }
    fetchMdas();
  }, []);

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getActionColor(action: string): string {
    const actionLower = action.toLowerCase();
    if (actionLower.includes('create') || actionLower.includes('upload')) return 'var(--success)';
    if (actionLower.includes('delete')) return 'var(--error)';
    if (actionLower.includes('update') || actionLower.includes('edit')) return 'var(--info)';
    if (actionLower.includes('login')) return 'var(--ghana-green)';
    if (actionLower.includes('logout')) return 'var(--text-muted)';
    if (actionLower.includes('approve')) return 'var(--success)';
    if (actionLower.includes('reject')) return 'var(--error)';
    return 'var(--text-secondary)';
  }

  function handleFilterChange(key: keyof AuditLogFilters, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function handleClearFilters() {
    setFilters({
      action: '',
      entityType: '',
      mdaId: '',
      search: '',
    });
    setPage(1);
  }

  const totalPages = Math.ceil(total / perPage);
  const hasFilters = filters.action || filters.entityType || filters.mdaId;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Audit Logs</h1>
          <p className={styles.subtitle}>Track all system activity and changes across institutions</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={fetchLogs} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Refresh
          </Button>
        </div>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.filters}>
        <select
          value={filters.action}
          onChange={(e) => handleFilterChange('action', e.target.value)}
          className={styles.filterSelect}
        >
          {ACTION_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        <select
          value={filters.entityType}
          onChange={(e) => handleFilterChange('entityType', e.target.value)}
          className={styles.filterSelect}
        >
          {ENTITY_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        <select
          value={filters.mdaId}
          onChange={(e) => handleFilterChange('mdaId', e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Institutions</option>
          {mdas.map((mda) => (
            <option key={mda.id} value={mda.id}>
              {mda.shortName}
            </option>
          ))}
        </select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        )}

        <div className={styles.filterSpacer} />

        <span className={styles.resultCount}>
          {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10,9 9,9 8,9" />
            </svg>
            <h3>No audit logs found</h3>
            <p>Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Institution</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <>
                  <tr
                    key={log.id}
                    className={expandedLog === log.id ? styles.expandedRow : ''}
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  >
                    <td className={styles.timestamp}>{formatDate(log.createdAt)}</td>
                    <td>
                      {log.user ? (
                        <div className={styles.userCell}>
                          <span className={styles.userName}>
                            {log.user.firstName} {log.user.lastName}
                          </span>
                          <span className={styles.userEmail}>{log.user.email}</span>
                        </div>
                      ) : (
                        <span className={styles.systemUser}>System</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={styles.actionBadge}
                        style={{ color: getActionColor(log.action) }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td>
                      <span className={styles.entityType}>{log.entityType}</span>
                      {log.entityId && (
                        <span className={styles.entityId}>{log.entityId.slice(0, 8)}...</span>
                      )}
                    </td>
                    <td>
                      {log.mda ? (
                        <span className={styles.mdaBadge}>{log.mda.code}</span>
                      ) : (
                        <span className={styles.platformBadge}>Platform</span>
                      )}
                    </td>
                    <td>
                      <button className={styles.expandBtn}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{
                            transform: expandedLog === log.id ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.2s',
                          }}
                        >
                          <polyline points="6,9 12,15 18,9" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {expandedLog === log.id && (
                    <tr key={`${log.id}-details`} className={styles.detailsRow}>
                      <td colSpan={6}>
                        <div className={styles.detailsContent}>
                          <div className={styles.detailsGrid}>
                            <div className={styles.detailItem}>
                              <span className={styles.detailLabel}>Log ID</span>
                              <span className={styles.detailValue}>{log.id}</span>
                            </div>
                            {log.entityId && (
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Entity ID</span>
                                <span className={styles.detailValue}>{log.entityId}</span>
                              </div>
                            )}
                            {log.ipAddress && (
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>IP Address</span>
                                <span className={styles.detailValue}>{log.ipAddress}</span>
                              </div>
                            )}
                            {log.userAgent && (
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>User Agent</span>
                                <span className={styles.detailValue}>{log.userAgent}</span>
                              </div>
                            )}
                          </div>
                          {log.details && (
                            <div className={styles.detailsJson}>
                              <span className={styles.detailLabel}>Additional Details</span>
                              <pre className={styles.jsonContent}>
                                {JSON.stringify(JSON.parse(log.details), null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
